import React from "react";
import { Button } from "@material-ui/core";
import { TextField } from "@material-ui/core";
import { FormControl } from "@material-ui/core";
import { FormLabel } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { CheckBoxSelector } from "./CheckboxSelector";
import moment from "moment";
import { SelectorDisplayItemProps, Selector } from "./Selector";
import { FILTER_TITLES, EVENT_ID_TITLES } from "../constants";
import { DateTimeFilter } from "./DateTimeFilter";
import { parsePositiveIntWithMaxLength } from "../utils";

export interface DateTimeFilteringProps {
	//Date for start date filter
	startDate: Date;
	//Date for end date filter
	endDate: Date;
	//The number of hours to set the start and end date apart on refresh. an input of 0 will use custom start and end dates.
	refreshDateTimespan: number;
}

export interface StatusFilteringProps {
	//The selected status for call logs.
	selectedStatus: string;
	//The statuses to populate the status dropdown selector with.
	statuses: SelectorDisplayItemProps[];
}

export interface EnvironmentFilteringProps {
	//The selected environment in the environment dropdown.
	selectedEnvironment: string;
	//All the environments available in the environments selector.
	environments: SelectorDisplayItemProps[];
}

export interface CallLogFilteringProps {
	//A call direction to search for (Twilio Calls List)
	selectedDirection: string;
	//The directions to populate the direction dropdown selector with
	directions: SelectorDisplayItemProps[];
	//A call duration to filter on (only return calls with duration >= value) (Twilio Calls List)
	duration: number;
	//To number for call logs
	toNumber: string;
	//From number for call logs
	fromNumber: string;
}

export interface ChannelLogFilteringProps {
	//The minimum number of members a channel should have to show up.
	memberCount: number;
	//The minimum number of messages a channel should have to show up.
	messagesCount: number;
	//The service to use for the selected service.
	selectedService: string;
}

export interface EventTypeFilteringProps {
	//The names of the eventTypes to be displayed in checkbox dropdown.
	eventTypeNames: string[];
	//The selected event type names.
	selectedEventTypeNames: string[];
}

export interface LogLevelFilteringProps {
	//The names of the event Ids (Perficient.Framework.Core.Interfaces.Events.logLevels) to be displayed in checkbox dropdown.
	logLevelValues: number[];
	//The selected event Ids.
	selectedLogLevels: number[];
}

export interface AvailabilityFilteringProps {
	//The select Availability in Availability dropdown
	selectedAvailability: string;
	//List of selectable availabilities
	selectableAvailabilities: SelectorDisplayItemProps[];
}

export interface FilterContainerProps {
	//Object containing event type filter properties.
	eventTypeFilter?: EventTypeFilteringProps;
	//object containing log level filter properties.
	logLevelFilter?: LogLevelFilteringProps;
	//Sid to search on
	searchSid?: string;
	//Correlation token to search on
	correlationToken?: string;
	//Object containing properties related to setting the date time filter values.
	dateFilter?: DateTimeFilteringProps;
	//Object containing properties related to the status filter.
	statusFilter?: StatusFilteringProps;
	//Object containing properties related to the environment filter.
	environmentFilter?: EnvironmentFilteringProps;
	//Object containing properties relating to the filters needed for fetching calls.
	callLogFilters?: CallLogFilteringProps;
	//Object containing properties relating to the filters needed for fetching channels.
	channelLogFilters?: ChannelLogFilteringProps;
	//Callback method to fetch list of workers to populate selector with for a specified environment. The first Item in the list of workers should always be 'Any'.
	fetchEnvironmentWorkers?: (env: string) => Promise<SelectorDisplayItemProps[]>;
	//Callback method used to fetch list of services for a specified environment.
	fetchEnvironmentServices?: (env: string) => Promise<SelectorDisplayItemProps[]>;
	//Callback method used to fetch list of workspaces for a specified accountSid.
	fetchAccountWorkspaces?: (env: string) => Promise<SelectorDisplayItemProps[]>;
	//initial services for the loaded environment.
	environmentServices?: SelectorDisplayItemProps[];
	//Initial workspaces for the loaded environment.
	environmentWorkspaces?: SelectorDisplayItemProps[];
	//The starting worker to select.
	selectedWorker?: string;
	//The starting workspace to select.
	selectedWorkspace?: string;
	//Friendly name to search on.
	searchFriendlyName?: string;
	// Object containing properties related to the availability filter.
	availabilityFilter?: AvailabilityFilteringProps;
	//Method that should accept an object which will contain new filter information to be dealt with by the page.
	onUpdate: (object) => void;
	//Method that should accept no paramaters, resets the filters for the page in local storage to the default value and returns that default object so that the filters can be set to those values.
	onReset?: () => object;
}

const useStyles = makeStyles((theme) => ({
	formControl: {
		marginLeft: theme.spacing(1),
		marginRight: theme.spacing(1),
		marginTop: "9px",
	},
	formControlLabel: {
		fontSize: theme.typography.caption.fontSize,
		margin: 0,
	},
	inputField: {
		width: 320,
		marginBottom: 0,
		marginTop: "11px",
	},
	inputFieldMedium: {
		width: 160,
	},
	inputFieldSmall: {
		width: 50,
	},
}));

/**
 * This component takes in optional props which correspond to various filters it can show. It will then return the new values for the filter if the 'apply filter' button is pressed.
 * @param props
 */
export const FilterContainer = (props: FilterContainerProps) => {
	const [update, changeUpdate] = React.useState(true);
	const [selectedEventTypeNames, changeSelectedEventTypeNames] = React.useState(
		props.eventTypeFilter?.selectedEventTypeNames,
	);
	const [selectedLogLevelValues, changeSelectedLogLevelValues] = React.useState(
		props.logLevelFilter?.selectedLogLevels,
	);
	const [searchSid, changeSearchSid] = React.useState(props.searchSid);
	const [correlationToken, changeCorrelationToken] = React.useState(props.correlationToken);

	const [toNumber, changeToNumber] = React.useState(props.callLogFilters?.toNumber);
	const [fromNumber, changeFromNumber] = React.useState(props.callLogFilters?.fromNumber);
	const [direction, changeDirection] = React.useState(props.callLogFilters?.selectedDirection);
	const [duration, changeDuration] = React.useState(props.callLogFilters?.duration);

	const [memberCount, changeMemberCount] = React.useState(props.channelLogFilters?.memberCount);
	const [messagesCount, changeMessagesCount] = React.useState(props.channelLogFilters?.messagesCount);
	const [selectedService, changeSelectedService] = React.useState(props.channelLogFilters?.selectedService);

	const [startDate, changeStartDate] = React.useState(props.dateFilter?.startDate);
	const [endDate, changeEndDate] = React.useState(props.dateFilter?.endDate);
	const [refreshDateTimespan, setRefreshDateTimespan] = React.useState(props.dateFilter?.refreshDateTimespan);

	const [selectedEnvironment, changeSelectedEnvironment] = React.useState(props.environmentFilter?.selectedEnvironment);
	const [selectedWorkspace, changeSelectedWorkspace] = React.useState(props.selectedWorkspace);
	const [status, changeStatus] = React.useState(props.statusFilter?.selectedStatus);
	const [selectedWorker, changeSelectedWorker] = React.useState(props.selectedWorker);
	const [workers, changeWorkers] = React.useState<SelectorDisplayItemProps[]>();
	const [services, changeServices] = React.useState<SelectorDisplayItemProps[] | undefined>(props.environmentServices);
	const [workspaces, changeWorkspaces] = React.useState<SelectorDisplayItemProps[] | undefined>(
		props.environmentWorkspaces,
	);
	const [searchFriendlyName, changeSearchFriendlyName] = React.useState(props.searchFriendlyName);
	const [selectedAvailability, changeSelectedAvailability] = React.useState(
		props.availabilityFilter?.selectedAvailability,
	);

	const [checkboxError, changeCheckboxError] = React.useState("");
	const [logLevelError, changeLogLevelError] = React.useState("");
	const [servicesError, changeServicesError] = React.useState("");

	const filterDisabled = !update;

	const classes = useStyles();

	function onUpdate() {
		const filterStateObject = {};
		if (props.eventTypeFilter) {
			filterStateObject["eventTypeNames"] = selectedEventTypeNames;
		}
		if (props.logLevelFilter) {
			filterStateObject["logLevelValues"] = selectedLogLevelValues;
		}
		if (props.searchSid != null) {
			filterStateObject["searchSid"] = searchSid;
		}
		if (props.correlationToken != null) {
			filterStateObject["correlationToken"] = correlationToken;
		}
		if (props.callLogFilters != null) {
			filterStateObject["toNumber"] = toNumber;
			filterStateObject["fromNumber"] = fromNumber;
			filterStateObject["duration"] = duration;
			filterStateObject["direction"] = direction;
		}
		if (props.channelLogFilters) {
			filterStateObject["memberCount"] = memberCount;
			filterStateObject["messagesCount"] = messagesCount;
		}
		if (props.dateFilter) {
			let startDateToSet = startDate;
			let endDateToSet = endDate;
			if (refreshDateTimespan !== 0) {
				startDateToSet = moment().subtract(refreshDateTimespan, "hours").toDate();
				changeStartDate(startDateToSet);
				endDateToSet = moment().toDate();
				changeEndDate(endDateToSet);
			}
			filterStateObject["startDate"] = startDateToSet;
			filterStateObject["endDate"] = endDateToSet;
			filterStateObject["refreshDateTimespan"] = refreshDateTimespan;
		}
		if (props.environmentFilter) {
			filterStateObject["selectedEnvironment"] = selectedEnvironment;
		}
		if (props.statusFilter) {
			filterStateObject["status"] = status;
		}
		if (props.fetchEnvironmentWorkers) {
			filterStateObject["selectedWorker"] = selectedWorker;
		}
		if (props.fetchEnvironmentServices) {
			filterStateObject["selectedService"] = selectedService;
		}
		if (props.fetchAccountWorkspaces) {
			filterStateObject["selectedWorkspace"] = selectedWorkspace;
		}
		if (props.searchFriendlyName != null) {
			filterStateObject["searchFriendlyName"] = searchFriendlyName;
		}
		if (props.availabilityFilter) {
			filterStateObject["selectedAvailability"] = selectedAvailability;
		}
		changeCheckboxError("");
		props.onUpdate(filterStateObject);
	}

	function onReset() {
		const defaultFilterObject = props.onReset!();
		changeSelectedEventTypeNames(defaultFilterObject["eventTypeNames"]);
		changeSelectedLogLevelValues(defaultFilterObject["logLevelValues"]);
		changeSearchSid(defaultFilterObject["searchSid"]);
		changeToNumber(defaultFilterObject["toNumber"]);
		changeFromNumber(defaultFilterObject["fromNumber"]);
		changeCorrelationToken(defaultFilterObject["correlationToken"]);
		changeDirection(defaultFilterObject["direction"]);
		changeDuration(defaultFilterObject["duration"]);
		changeStartDate(defaultFilterObject["startDate"]);
		changeEndDate(defaultFilterObject["endDate"]);
		setRefreshDateTimespan(defaultFilterObject["refreshDateTimespan"]);
		changeSelectedEnvironment(
			(props.environmentFilter && props.environmentFilter.environments[0].valueString) || undefined,
		);
		changeSelectedWorkspace(defaultFilterObject["workspace"]);
		changeStatus(defaultFilterObject["status"]);
		changeSelectedWorker(defaultFilterObject["selectedWorker"]);
		changeSelectedService(defaultFilterObject["selectedService"]);
		changeServices(defaultFilterObject["environmentServices"]);
		changeMemberCount(defaultFilterObject["memberCount"]);
		changeMessagesCount(defaultFilterObject["messagesCount"]);
		changeSearchFriendlyName(defaultFilterObject["searchFriendlyName"]);
		changeSelectedAvailability(defaultFilterObject["selectedAvailability"]);
	}

	function handleCheckBoxChange(newFilters: string[]) {
		if (newFilters.length > 0) {
			changeSelectedEventTypeNames(newFilters);
			changeUpdate(true);
			changeCheckboxError("");
		} else {
			changeCheckboxError("At least one event type must be selected.");
		}
	}

	function handlelogLevelChange(newFilters: number[]) {
		if (newFilters.length > 0) {
			changeSelectedLogLevelValues(newFilters);
			changeUpdate(true);
			changeLogLevelError("");
		} else {
			changeLogLevelError("At least one event Id must be selected.");
		}
	}

	function handleSearchSidChange(value) {
		changeSearchSid(value);
		changeUpdate(true);
	}

	function handleToNumberChange(value) {
		changeToNumber(value);
		changeUpdate(true);
	}

	function handleFromNumberChange(value) {
		changeFromNumber(value);
		changeUpdate(true);
	}

	function handleCorrelationTokenChange(newCorrelationToken: string) {
		changeCorrelationToken(newCorrelationToken);
		changeUpdate(true);
	}

	function handleDurationChange(newDuration: number) {
		changeDuration(newDuration);
		changeUpdate(true);
	}

	function handleDirectionChange(newDirection: string) {
		changeDirection(newDirection);
		changeUpdate(true);
	}

	function handleDateChange(newStartDate: Date, newEndDate: Date) {
		if (!newStartDate || !newEndDate) {
			if (!newStartDate) {
				changeUpdate(false);
			}
			if (!newEndDate) {
				changeUpdate(false);
			}
		} else if (newStartDate < newEndDate) {
			changeStartDate(newStartDate);
			changeEndDate(newEndDate);
		}
	}

	function handleMemberCountChange(newMemberCount) {
		changeMemberCount(newMemberCount);
		changeUpdate(true);
	}

	function handleMessagesCountChange(newMessagesCount) {
		changeMessagesCount(newMessagesCount);
		changeUpdate(true);
	}

	async function loadWorkers() {
		if (props.fetchEnvironmentWorkers) {
			changeWorkers(await props.fetchEnvironmentWorkers!(selectedEnvironment || ""));
		}
	}

	async function loadServices() {
		if (props.fetchEnvironmentServices) {
			const newServices = await props.fetchEnvironmentServices(selectedEnvironment || "");
			changeServices(newServices);
			if (newServices.length > 0) {
				changeSelectedService(newServices[0].valueString || "");
				changeServicesError("");
			} else {
				changeUpdate(false);
				changeServicesError("There are no services for the selected environment.");
			}
		}
	}

	async function loadWorkspaces() {
		if (props.fetchAccountWorkspaces) {
			const newWorkspaces = await props.fetchAccountWorkspaces(selectedEnvironment || "");
			changeWorkspaces(newWorkspaces);
			if (newWorkspaces.length > 0) {
				changeSelectedWorkspace(newWorkspaces[0].valueString || "");
			} else {
				changeUpdate(false);
			}
		}
	}

	async function handleEnvironmentChange(newEnvironment) {
		changeSelectedEnvironment(newEnvironment);
		changeUpdate(true);
		changeWorkers(undefined);
		changeSelectedWorker(undefined);
		changeServices(undefined);
		changeSelectedService(undefined);
	}

	async function handleWorkspaceChange(newWorkspace) {
		changeSelectedWorkspace(newWorkspace);
		changeUpdate(true);
	}

	function handleStatusChange(newStatus) {
		changeStatus(newStatus);
		changeUpdate(true);
	}

	function handleWorkerChange(newWorker) {
		if (newWorker !== "Any") {
			changeSelectedWorker(newWorker);
		} else {
			changeUpdate(true);
		}
	}

	function handleServiceChange(newService) {
		changeSelectedService(newService);
		changeUpdate(true);
	}

	function handleSearchFriendlyNameChange(value) {
		changeSearchFriendlyName(value);
		changeUpdate(true);
	}

	function handleAvailabilityChange(newAvailability) {
		changeSelectedAvailability(newAvailability);
		changeUpdate(true);
	}

	function eventTypeFilter() {
		if (props.eventTypeFilter) {
			return (
				<span style={{ marginLeft: 10 }} data-testid={"filter-container-event-types"}>
					<CheckBoxSelector
						label={"Event Types"}
						errorMessage={checkboxError}
						filters={props.eventTypeFilter.eventTypeNames}
						selectedFilters={selectedEventTypeNames || [""]}
						onFilterChange={handleCheckBoxChange}
						filterNames={FILTER_TITLES}
					></CheckBoxSelector>
				</span>
			);
		}
	}

	function logLevelFilter() {
		if (props.logLevelFilter) {
			return (
				<span style={{ marginLeft: 15 }} data-testid={"filter-container-event-ids"}>
					<CheckBoxSelector
						label={"Log Level"}
						errorMessage={logLevelError}
						filters={props.logLevelFilter.logLevelValues}
						selectedFilters={selectedLogLevelValues || [""]}
						onFilterChange={handlelogLevelChange}
						filterNames={EVENT_ID_TITLES}
					></CheckBoxSelector>
				</span>
			);
		}
	}

	function searchSidFilter() {
		if (props.searchSid != null) {
			return (
				<span data-testid={"filter-container-search-sid"}>
					<FormControl className={classes.formControl}>
						<FormLabel className={classes.formControlLabel}>Sid:</FormLabel>
						<TextField
							className={classes.inputField}
							value={searchSid}
							margin="normal"
							onChange={(e) => handleSearchSidChange(e.target.value)}
						/>
					</FormControl>
				</span>
			);
		}
	}

	function correlationTokenFilter() {
		if (props.correlationToken != null) {
			return (
				<span data-testid={"filter-container-correlation-token"}>
					<FormControl className={classes.formControl}>
						<FormLabel className={classes.formControlLabel}>Correlation Token:</FormLabel>
						<TextField
							className={classes.inputField}
							value={correlationToken}
							margin="normal"
							onChange={(e) => handleCorrelationTokenChange(e.target.value)}
						/>
					</FormControl>
				</span>
			);
		}
	}

	function callLogFilters() {
		if (props.callLogFilters) {
			return (
				<div style={{ display: "flex" }}>
					<span data-testid={"filter-container-to-number"}>
						<FormControl className={classes.formControl}>
							<FormLabel className={classes.formControlLabel}>To #</FormLabel>
							<TextField
								className={`${classes.inputField} ${classes.inputFieldMedium}`}
								value={toNumber}
								margin="normal"
								onChange={(e) => handleToNumberChange(e.target.value)}
							/>
						</FormControl>
					</span>
					<span data-testid={"filter-container-from-number"}>
						<FormControl className={classes.formControl}>
							<FormLabel className={classes.formControlLabel}>From #</FormLabel>
							<TextField
								className={`${classes.inputField} ${classes.inputFieldMedium}`}
								value={fromNumber}
								margin="normal"
								onChange={(e) => handleFromNumberChange(e.target.value)}
							/>
						</FormControl>
					</span>
					<span data-testid={"filter-container-direction-filter"}>
						<Selector
							items={props.callLogFilters.directions}
							initialValue={direction || "Any"}
							updateSelected={handleDirectionChange}
							label={"Direction"}
						></Selector>
					</span>
					<span data-testid={"filter-container-duration-filter"}>
						<FormControl className={classes.formControl}>
							<FormLabel className={classes.formControlLabel}>Duration:</FormLabel>
							<TextField
								type="number"
								className={`${classes.inputField} ${classes.inputFieldSmall}`}
								inputProps={{ min: "0" }}
								value={duration!.toString()}
								margin="normal"
								onChange={(e) => handleDurationChange(parsePositiveIntWithMaxLength(e.target.value, 3))}
							/>
						</FormControl>
					</span>
				</div>
			);
		}
	}

	function dateFilter() {
		if (props.dateFilter) {
			return (
				<span
					style={{ marginLeft: 15, marginTop: 5, alignSelf: "center" }}
					data-testid={"filter-container-date-filter"}
				>
					<DateTimeFilter
						startDate={startDate || moment().toDate()}
						endDate={endDate || moment().toDate()}
						refreshDateTimespan={refreshDateTimespan!}
						setRefreshThresholdFunction={setRefreshDateTimespan}
						setDates={handleDateChange}
					></DateTimeFilter>
				</span>
			);
		}
	}
	function channelLogFilters() {
		if (props.channelLogFilters) {
			return (
				<span>
					<span style={{ marginLeft: 10 }} data-testid={"filter-container-memebersCount-filter"}>
						<FormControl className={classes.formControl}>
							<FormLabel className={classes.formControlLabel}>Min Members:</FormLabel>
							<TextField
								type="number"
								className={`${classes.inputField} ${classes.inputFieldSmall}`}
								inputProps={{ min: "0" }}
								value={memberCount!.toString()}
								margin="normal"
								onChange={(e) => handleMemberCountChange(parsePositiveIntWithMaxLength(e.target.value, 3))}
							/>
						</FormControl>
					</span>
					<span style={{ marginLeft: 10 }} data-testid={"filter-container-messagesCount-filter"}>
						<FormControl className={classes.formControl}>
							<FormLabel className={classes.formControlLabel}>Min Messages:</FormLabel>
							<TextField
								type="number"
								className={`${classes.inputField} ${classes.inputFieldSmall}`}
								inputProps={{ min: "0" }}
								value={messagesCount!.toString()}
								margin="normal"
								onChange={(e) => handleMessagesCountChange(parsePositiveIntWithMaxLength(e.target.value, 3))}
							/>
						</FormControl>
					</span>
				</span>
			);
		}
	}

	function environmentFilter() {
		if (props.environmentFilter) {
			return (
				<span data-testid={"filter-container-environment-filter"}>
					<Selector
						items={props.environmentFilter.environments}
						initialValue={selectedEnvironment || "No Environments found."}
						updateSelected={handleEnvironmentChange}
						label={"Environments"}
					></Selector>
				</span>
			);
		}
	}

	function workspaceFilter() {
		if (props.fetchAccountWorkspaces) {
			if (!workspaces) {
				loadWorkspaces();
			} else {
				if (workspaces.length > 0 && !selectedWorkspace) {
					changeSelectedWorkspace(workspaces[0].valueString || "");
				}
				return (
					<span data-testid={"filter-container-workspace-filter"}>
						<Selector
							items={workspaces || []}
							initialValue={selectedWorkspace || ""}
							updateSelected={handleWorkspaceChange}
							label={"Workspaces"}
						></Selector>
					</span>
				);
			}
		}
	}

	function statusFilter() {
		if (props.statusFilter) {
			return (
				<span data-testid={"filter-container-status-filter"}>
					<Selector
						items={props.statusFilter.statuses}
						initialValue={status || "No Statuses Provided"}
						updateSelected={handleStatusChange}
						label={"Status"}
					></Selector>
				</span>
			);
		}
	}

	function workerFilter() {
		if (props.fetchEnvironmentWorkers) {
			if (!workers) {
				loadWorkers();
			} else {
				return (
					<Selector
						data-testid={"filter-container-worker-filter"}
						helperMessage={selectedWorker !== "Any" ? selectedWorker : ""}
						items={workers || []}
						initialValue={selectedWorker || "Any"}
						updateSelected={handleWorkerChange}
						label={"Workers"}
					></Selector>
				);
			}
		}
	}

	function serviceFilter() {
		if (props.fetchEnvironmentServices) {
			if (!services) {
				loadServices();
			} else {
				if (services.length > 0 && selectedService == "") {
					changeSelectedService(services[0].valueString || "");
				}
				return (
					<span style={{ marginLeft: 10 }} data-testid={"filter-container-service-filter"}>
						<Selector
							errorMessage={servicesError}
							helperMessage={selectedService || ""}
							items={services || []}
							initialValue={selectedService || ""}
							updateSelected={handleServiceChange}
							label={"Services"}
						></Selector>
					</span>
				);
			}
		}
	}

	function searchFriendlyNameFilter() {
		if (props.searchFriendlyName != null) {
			return (
				<span data-testid={"filter-container-search-friendly-name"}>
					<FormControl className={classes.formControl}>
						<FormLabel className={classes.formControlLabel}>Friendly Name:</FormLabel>
						<TextField
							className={classes.inputField}
							value={searchFriendlyName}
							margin="normal"
							onChange={(e) => handleSearchFriendlyNameChange(e.target.value)}
						/>
					</FormControl>
				</span>
			);
		}
	}

	function availabilityFilter() {
		if (props.availabilityFilter) {
			return (
				<span data-testid={"filter-container-availability-filter"}>
					<Selector
						items={props.availabilityFilter.selectableAvailabilities}
						initialValue={selectedAvailability || "any"}
						updateSelected={handleAvailabilityChange}
						label="Availability"
					></Selector>
				</span>
			);
		}
	}

	return (
		<div style={{ display: "flex", justifyContent: "flex-start", maxWidth: 1210 }}>
			<div style={{ display: "flex", justifyContent: "flex-start", flexWrap: "wrap" }}>
				{environmentFilter()}
				{workspaceFilter()}
				{statusFilter()}
				{workerFilter()}
				{serviceFilter()}
				{searchSidFilter()}
				{correlationTokenFilter()}
				{callLogFilters()}
				{channelLogFilters()}
				{eventTypeFilter()}
				{logLevelFilter()}
				{dateFilter()}
				{searchFriendlyNameFilter()}
				{availabilityFilter()}
			</div>
			<div style={{ margin: "auto" }}>
				<Button
					style={{ height: 35, minWidth: 150, maxWidth: 200, alignSelf: "center", margin: "10px" }}
					size="small"
					variant="contained"
					color="primary"
					onClick={onUpdate}
					disabled={filterDisabled}
				>
					Refresh Results
				</Button>
				<Button
					style={{ height: 35, minWidth: 150, maxWidth: 200, alignSelf: "center", margin: "10px" }}
					size="small"
					variant="contained"
					hidden={props.onReset === undefined}
					onClick={onReset}
				>
					Reset Filters
				</Button>
			</div>
		</div>
	);
};
